import React from "react";
import type { PhotoAiPreviewItem } from "../../types/photoAiEdit";

type PhotoAiPreviewCompareCardProps = {
  preview: PhotoAiPreviewItem;
  fallbackOriginalUrl?: string;
  onApply: (preview: PhotoAiPreviewItem) => void;
  onDiscard: (previewId: number) => void;
  onTryAgain: (preview: PhotoAiPreviewItem) => void;
  applyLabel?: string;
  className?: string;
  /** Larger images when shown inside the full-screen preview modal */
  variant?: "inline" | "modal";
};

function getPreviewTitle(preview: PhotoAiPreviewItem): string {
  if (preview.type === "custom_edit") {
    return "Edição personalizada (IA)";
  }
  return "Mobília virtual (IA)";
}

export default function PhotoAiPreviewCompareCard({
  preview,
  fallbackOriginalUrl,
  onApply,
  onDiscard,
  onTryAgain,
  applyLabel = "Confirmar (adicionar foto)",
  className = "",
  variant = "inline",
}: PhotoAiPreviewCompareCardProps) {
  const originalUrl = preview.original_url || fallbackOriginalUrl || "";
  const imageHeightClass =
    variant === "modal"
      ? "h-[min(42vh,340px)]"
      : "h-[min(32vh,260px)]";

  return (
    <div
      className={`flex flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-sm ${className}`}
    >
      {preview.type === "custom_edit" && preview.improvement_summary && (
        <p className="mb-3 text-sm text-slate-600">
          <span className="font-medium text-slate-800">Alterações: </span>
          {preview.improvement_summary}
          {preview.from_cache && (
            <span className="ml-2 rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
              cache
            </span>
          )}
        </p>
      )}

      <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="min-w-0">
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Original
          </p>
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
            <img
              src={originalUrl}
              alt="Imagem original"
              className={`${imageHeightClass} w-full object-contain`}
            />
          </div>
        </div>
        <div className="min-w-0">
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {getPreviewTitle(preview)}
          </p>
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
            <img
              src={preview.url}
              alt="Imagem editada por IA"
              className={`${imageHeightClass} w-full object-contain`}
            />
          </div>
        </div>
      </div>

      {preview.type === "custom_edit" && preview.user_prompt && (
        <p className="mb-3 text-xs text-slate-500">
          Prompt: &quot;{preview.user_prompt}&quot;
        </p>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <button
          type="button"
          onClick={() => onApply(preview)}
          className="flex-1 rounded-lg border-0 bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 sm:min-w-[140px]"
        >
          {applyLabel}
        </button>
        <button
          type="button"
          onClick={() => onDiscard(preview.id)}
          className="flex-1 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 sm:min-w-[120px]"
        >
          Descartar
        </button>
        {preview.type === "custom_edit" && (
          <button
            type="button"
            onClick={() => onTryAgain(preview)}
            className="flex-1 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-900 transition hover:bg-amber-100 sm:min-w-[140px]"
          >
            Tentar novamente
          </button>
        )}
      </div>
    </div>
  );
}
